import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import profileImage from "../../../../images/user/Default_pfp.png";
import TruncateUserName from '../../../helpers/truncateUserName';

// Skeleton loader component for the table
const TableSkeletonLoader = ({ rows = 10 }) => {
    return (
        <div className="p-datatable p-component p-datatable-responsive-scroll">
            <div className="p-datatable-wrapper">
                <table
                    className="p-datatable-table"
                    style={{
                        minWidth: '50rem',
                        width: '100%',
                        textAlign: 'left',
                        borderSpacing: 0,
                        borderCollapse: 'collapse',
                        borderRadius: '15px',
                        overflow: 'hidden',
                    }}
                >
                    <thead className="p-datatable-thead">
                        <tr>
                            <th style={{ 
                                backgroundColor: '#F5F6F8',
                                padding: '10px 0',
                                fontWeight: 'bold',
                                textAlign: 'left',
                                width: '30%',
                                paddingLeft: '10px',
                                paddingRight: '20px',
                                color: '#6a7988',
                                fontSize: '17px',
                                fontWeight: 500,
                            }}>Client Name</th>
                            <th style={{ 
                                backgroundColor: '#F5F6F8',
                                padding: '10px 0',
                                fontWeight: 'bold',
                                textAlign: 'left',
                                width: '30%',
                                paddingLeft: '10px',
                                paddingRight: '20px',
                                color: '#6a7988',
                                fontSize: '17px',
                                fontWeight: 500,
                            }}>Account Manager</th>
                            <th style={{ 
                                backgroundColor: '#F5F6F8',
                                padding: '10px 0',
                                fontWeight: 'bold',
                                textAlign: 'left',
                                width: '10%',
                                paddingLeft: '10px',
                                paddingRight: '20px',
                                color: '#6a7988',
                                fontSize: '17px',
                                fontWeight: 500,
                            }}>Client Status</th>
                        </tr>
                    </thead>
                    <tbody className="p-datatable-tbody" style={{ marginBottom: 0, paddingBottom: 0 }}>
                        {Array.from({ length: rows }).map((_, index) => (
                            <tr key={`skeleton-${index}`}>
                                {/* Client Name */}
                                <td style={{ 
                                    padding: '25px 10px',
                                    paddingRight: '20px',
                                    borderBottom: '0.2px solid rgba(106, 121, 136, 0.7)',
                                    color: '#282828',
                                    fontWeight: 300,
                                    fontSize: '18px',
                                    lineHeight: '22px',
                                    background: '#fff',
                                }}>
                                    <div
                                        style={{
                                            width: '180px',
                                            height: '22px',
                                            backgroundColor: '#e5e7eb',
                                            borderRadius: '4px',
                                            animation: 'pulse 1.5s ease-in-out infinite',
                                        }}
                                    />
                                </td>
                                {/* Account Manager */}
                                <td style={{ 
                                    padding: '25px 10px',
                                    paddingRight: '20px',
                                    borderBottom: '0.2px solid rgba(106, 121, 136, 0.7)',
                                    color: '#282828',
                                    fontWeight: 300,
                                    fontSize: '18px',
                                    lineHeight: '22px',
                                    background: '#fff',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div
                                            style={{
                                                width: '27px',
                                                height: '27px',
                                                backgroundColor: '#e5e7eb',
                                                borderRadius: '50%',
                                                marginRight: '15px',
                                                animation: 'pulse 1.5s ease-in-out infinite',
                                            }}
                                        />
                                        <div
                                            style={{
                                                width: '150px',
                                                height: '22px',
                                                backgroundColor: '#e5e7eb',
                                                borderRadius: '4px',
                                                animation: 'pulse 1.5s ease-in-out infinite',
                                            }}
                                        />
                                    </div>
                                </td>
                                {/* Client Status */}
                                <td style={{ 
                                    padding: '25px 10px',
                                    paddingRight: '20px',
                                    borderBottom: '0.2px solid rgba(106, 121, 136, 0.7)',
                                    color: '#282828',
                                    fontWeight: 300,
                                    fontSize: '18px',
                                    lineHeight: '22px',
                                    background: '#fff',
                                }}>
                                    <div
                                        style={{
                                            width: '80px',
                                            height: '31px',
                                            backgroundColor: '#e5e7eb',
                                            borderRadius: '100px',
                                            animation: 'pulse 1.5s ease-in-out infinite',
                                        }}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
        </div>
    );
};

const ClientDataTable = ({ clients, clientLoading }) => {
    const reportingClient = useSelector((state) => state.reportingClient);
    const [filteredData, setFilteredData] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [hasInitialized, setHasInitialized] = useState(false);

    useEffect(() => {
        try {
            // Reset initialization state when clients change
            setHasInitialized(false);
            
            // Validate input array
            if (!Array.isArray(clients)) {
                setFilteredData([]);
                setHasInitialized(true);
                return;
            }

            const filterClients = () => {
                try {
                    const filteredClients = clients.filter((client) => {
                        if (!client || !client._id) return false;

                        const isClientDetailMatch =
                            !reportingClient?.clientDetails ||
                            (Array.isArray(reportingClient.clientDetails) && reportingClient.clientDetails.includes(client._id));
                        
                        const isAccountManagerMatch =
                            !reportingClient?.clientAccountManager ||
                            (Array.isArray(reportingClient.clientAccountManager) &&
                                client.acc_manager &&
                                client.acc_manager._id &&
                                reportingClient.clientAccountManager.includes(client.acc_manager._id));
                        
                        const isStatusMatch =
                            !reportingClient?.clientStatus ||
                            (Array.isArray(reportingClient.clientStatus) && reportingClient.clientStatus.includes(client.is_active));
                        
                        return isClientDetailMatch && isAccountManagerMatch && isStatusMatch;
                    });

                    setFilteredData(filteredClients || []);
                    setHasInitialized(true);
                } catch (error) {
                    console.error('Error filtering clients:', error);
                    setFilteredData([]);
                    setHasInitialized(true);
                }
            };

            // Use setTimeout for non-blocking execution
            const timeoutId = setTimeout(() => {
                filterClients();
            }, 0);

            return () => clearTimeout(timeoutId); // Cleanup timeout
        } catch (error) {
            console.error('Error in useEffect:', error);
            setFilteredData([]);
            setHasInitialized(true);
        }
    }, [clients, reportingClient]);

    const onPageChange = (e) => {
        try {
            if (e && typeof e.first === 'number' && rowsPerPage > 0) {
                setCurrentPage(e.first / rowsPerPage);
            }
        } catch (error) {
            console.error('Error in onPageChange:', error);
        }
    };

    const renderClientName = (rowData) => {
        try {
            if (!rowData) return 'N/A';
            return rowData.client_name || rowData.legal_entity_name || 'N/A';
        } catch (error) {
            console.error('Error rendering client name:', error);
            return 'N/A';
        }
    };

    const renderAccountManager = (rowData) => {
        try {
            if (!rowData || !rowData.acc_manager) {
                return (
                    <div className='member-name' style={{ display: 'flex', alignItems: 'center' }}>
                        <img src={profileImage} alt="Default" className="member-image" />
                        <span>N/A</span>
                    </div>
                );
            }

            const accountManager = rowData.acc_manager;
            const fullName = accountManager?.first_name
                ? `${accountManager.first_name}${accountManager.middle_name && accountManager.middle_name !== "null" ? " " + accountManager.middle_name : ""} ${accountManager.last_name || ""}`
                : accountManager?.company_name || "N/A";

            return (
                <div className='member-name' style={{ display: 'flex', alignItems: 'center' }}>
                    {accountManager?.profile_image && accountManager.profile_image !== "null" ? (
                        <img
                            src={`/uploads/lowProfileImage/${accountManager.profile_image}`}
                            alt={fullName}
                            className="member-image"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/uploads/lowProfileImage/user_default.png';
                            }}
                        />
                    ) : (
                        <img src={profileImage} alt={fullName} className="member-image" />
                    )}
                    <TruncateUserName
                        textContent={accountManager?.first_name ? (
                            <>
                                {accountManager.first_name}{' '}
                                {accountManager.middle_name && accountManager.middle_name !== 'null' ? accountManager.middle_name + ' ' : ''}
                                {accountManager.last_name || ''}{' '}
                            </>
                        ) : (
                            <>
                                {accountManager?.company_first_name
                                    ? `${accountManager.company_first_name} ${accountManager.company_last_name || ''}`
                                    : `${accountManager?.company_name || ''}(Company)`}
                            </>
                        )}
                    />
                </div>
            );
        } catch (error) {
            console.error('Error rendering account manager:', error);
            return (
                <div className='member-name' style={{ display: 'flex', alignItems: 'center' }}>
                    <img src={profileImage} alt="Default" className="member-image" />
                    <span>N/A</span>
                </div>
            );
        }
    };

    const renderStatus = (rowData) => {
        try {
            if (!rowData) {
                return <span className="client-status payment-status inactive">N/A</span>;
            }

            const status = rowData.is_active === true ? 'Active' : 'Inactive';
            const statusClass = status === 'Active' ? 'payment-status manual' : 'payment-status inactive';
            return <span className={`client-status ${statusClass}`}>{status}</span>;
        } catch (error) {
            console.error('Error rendering status:', error);
            return <span className="client-status payment-status inactive">N/A</span>;
        }
    };

    // Show skeleton loader while loading
    if (clientLoading) {
        return (
            <div className="datatable-team-maintb">
                <TableSkeletonLoader rows={10} />
            </div>
        );
    }

    try {
        // Ensure filteredData is an array
        const safeFilteredData = Array.isArray(filteredData) ? filteredData : [];
        const shouldShowPaginator = safeFilteredData.length > rowsPerPage;
        // Only show empty message if we've initialized and there's no data
        const emptyMessage = hasInitialized && safeFilteredData.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 24px", gap: "10px" }}>
            <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="6" y="10" width="32" height="26" rx="3" stroke="#D0D5DD" strokeWidth="2" />
              <path d="M6 16h32" stroke="#D0D5DD" strokeWidth="2" />
              <path d="M14 7v6M30 7v6" stroke="#D0D5DD" strokeWidth="2" strokeLinecap="round" />
              <path d="M13 24h6M13 29h12" stroke="#BDBDBD" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <p style={{ margin: 0, fontSize: "15px", fontWeight: "500", color: "#282828" }}>No clients found</p>
            <p style={{ margin: 0, fontSize: "13px", color: "#96A8BA" }}>Try adjusting your filters.</p>
          </div>
        ) : "";

        return (
            <div className="datatable-team-maintb">
                <DataTable
                    value={safeFilteredData}
                    tableStyle={{ minWidth: '50rem', width: '100%', textAlign: 'left' }}
                    paginator={shouldShowPaginator}
                    paginatorClassName="recent-invoices-pagination"
                    rows={rowsPerPage}
                    first={currentPage * rowsPerPage}
                    onPage={onPageChange}
                    emptyMessage={emptyMessage}
                >
                    <Column
                        field="client_name"
                        header="Client Name"
                        body={renderClientName}
                        sortable
                        style={{ width: '30%', paddingRight: '20px' }}
                    />
                    <Column
                        field="acc_manager"
                        body={renderAccountManager}
                        header="Account Manager"
                        sortable
                        style={{ width: '30%', paddingRight: '20px' }}
                    />
                    <Column
                        field="is_active"
                        body={renderStatus}
                        header="Client Status"
                        sortable
                        style={{ width: '10%', paddingRight: '20px' }}
                    />
                </DataTable>
            </div>
        );
    } catch (error) {
        console.error('Error rendering ClientDataTable:', error);
        return (
            <div className="datatable-team-maintb">
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <p>Error loading client data. Please try again.</p>
                </div>
            </div>
        );
    }
};

export default ClientDataTable;
